import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardCheck, Users, Calendar, CheckCircle, Plus, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from '../utils/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HospitalEvaluationDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    total_evaluations: 0,
    pending_evaluations: 0,
    in_progress_evaluations: 0,
    completed_evaluations: 0,
    recent_evaluations: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check if user has access
      if (!['Super Admin', 'Exlva Admin', 'Hospital Admin'].includes(parsedUser.role)) {
        toast.error('Access denied');
        navigate('/dashboard');
        return;
      }
    }
    
    fetchDashboardStats();
  }, [navigate]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/evaluations/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        label: 'Pending', 
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      in_progress: { 
        label: 'In Progress', 
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Clock
      },
      completed: { 
        label: 'Responded', 
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.className} border flex items-center gap-1 px-2 py-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center">Loading dashboard...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Evaluation Management</h1>
            <p className="text-gray-600 mt-1">
              {user?.role === 'Hospital Admin' 
                ? `Manage evaluations for ${user?.institution_name || 'your institution'}`
                : 'Manage evaluations across all institutions'
              }
            </p>
          </div>
          <Button 
            className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
            onClick={() => navigate('/evaluations/trigger')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Initiate Evaluation
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Initiated</p>
                  <p className="text-3xl font-bold mt-2">{stats.total_evaluations}</p>
                </div>
                <ClipboardCheck className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm font-medium">Pending</p>
                  <p className="text-3xl font-bold mt-2">{stats.pending_evaluations}</p>
                </div>
                <Calendar className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">In Progress</p>
                  <p className="text-3xl font-bold mt-2">{stats.in_progress_evaluations}</p>
                </div>
                <Clock className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Responded</p>
                  <p className="text-3xl font-bold mt-2">{stats.completed_evaluations}</p>
                </div>
                <CheckCircle className="w-12 h-12 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Evaluation Log Table */}
        <Card>
          <CardHeader>
            <h2 className="text-xl font-semibold text-gray-900">Evaluation Cycle Log</h2>
            <p className="text-sm text-gray-600 mt-1">
              Track all evaluation cycles and their response status
            </p>
          </CardHeader>
          <CardContent>
            {stats.recent_evaluations && stats.recent_evaluations.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Cycle ID
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Initiated On
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Initiated By
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Reporting Manager
                      </th>
                      {user?.role !== 'Hospital Admin' && (
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                          Institution
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                        Response Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stats.recent_evaluations.map((evaluation, index) => (
                      <tr 
                        key={evaluation.instance_id} 
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-4">
                          <span className="font-mono text-xs text-gray-600">
                            #{evaluation.instance_id.slice(0, 8)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {formatDate(evaluation.created_at)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-700">
                          {evaluation.triggered_by_name || 'Admin'}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <Users className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="font-medium text-gray-900">
                              {evaluation.evaluator_name || evaluation.target_name}
                            </span>
                          </div>
                        </td>
                        {user?.role !== 'Hospital Admin' && (
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {evaluation.hospital_name || 'N/A'}
                          </td>
                        )}
                        <td className="px-4 py-4">
                          {getStatusBadge(evaluation.status)}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {evaluation.status === 'completed' && evaluation.updated_at
                            ? formatDate(evaluation.updated_at)
                            : '-'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <ClipboardCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg font-medium">No evaluations initiated yet</p>
                <p className="text-gray-400 text-sm mt-2">
                  Click "Initiate Evaluation" to create your first evaluation cycle
                </p>
                <Button
                  className="mt-4 bg-[#0056B3] hover:bg-[#0056B3]/90"
                  onClick={() => navigate('/evaluations/trigger')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Initiate First Evaluation
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              How Evaluation Works
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Initiate:</strong> Trigger evaluation cycles for selected Reporting Managers</li>
              <li>• <strong>Pending:</strong> Managers receive notification to complete evaluation</li>
              <li>• <strong>In Progress:</strong> Managers are actively filling the evaluation form</li>
              <li>• <strong>Responded:</strong> Managers have submitted their evaluation</li>
              <li>• <strong>Institution-Scoped:</strong> You can only manage evaluations for your institution</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
